import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
	EngineRubricSkill,
	EngineRubricSkillCreatePayload,
	EngineRubricSkillUpdatePayload,
	Rubric,
} from '@mira-avant/mtlibs';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { FormTitleComponent } from '../../../ui/forms/form-title/form-title.component';
import { TextareaFieldComponent } from '../../../fields/standard/textarea-field/textarea-field.component';
import { RubricDropdownFieldComponent } from '../../../fields/standard/rubric-dropdown-field/rubric-dropdown-field.component';
import { Subscription } from 'rxjs';
import { EngineDropdownField } from '../../../fields/standard/engine-dropdown-field/engine-dropdown-field';
import { SkillCodeDropdownFieldComponent } from '../../../fields/standard/skill-code-dropdown-field/skill-code-dropdown-field.component';

@Component({
	selector: 'app-engine-rubric-skill-form',
	standalone: true,
	imports: [
		AppModule,
		ZorroModule,
		ThirdPartyModule,
		AngularModule,
		FormTitleComponent,
		TextareaFieldComponent,
		RubricDropdownFieldComponent,
		EngineDropdownField,
		SkillCodeDropdownFieldComponent,
	],
	templateUrl: './engine-rubric-skill-form.component.html',
	styleUrl: './engine-rubric-skill-form.component.scss',
})
export class EngineRubricSkillFormComponent implements OnInit, OnChanges, OnDestroy {
	@Input('rubric') public rubric: Rubric | undefined | null;
	@Input('engineRubricSkill') public engineRubricSkill: EngineRubricSkill | undefined | null;
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<
		EngineRubricSkillCreatePayload | EngineRubricSkillUpdatePayload
	> = new EventEmitter<EngineRubricSkillCreatePayload | EngineRubricSkillUpdatePayload>();

	public form: FormGroup = this.fb.group({
		engine_name: ['', [Validators.required, Validators.maxLength(256)]],
		rubric_code: ['', [Validators.required, Validators.maxLength(255)]],
		skill_code: ['', [Validators.required, Validators.maxLength(255)]],
		assessment: ['', [Validators.required]],
	});

	private subscriptions: Subscription = new Subscription();

	constructor(private fb: FormBuilder) {}

	ngOnInit(): void {
		// No subscriptions needed, as rubric_code uses app-rubric-dropdown-field
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['engineRubricSkill'] && this.engineRubricSkill) {
			this.form.patchValue({
				engine_name: this.engineRubricSkill.engine_name,
				rubric_code: this.engineRubricSkill.rubric_code,
				skill_code: this.engineRubricSkill.skill_code,
				assessment: this.engineRubricSkill.assessment,
			});
		} else if (changes['rubric'] && this.rubric) {
			this.form.patchValue({
				rubric_code: this.rubric.code,
			});
		}
	}

	public async onSubmit(): Promise<void> {
		if (this.form.invalid) {
			console.error('EngineRubricSkill form is invalid', this.form.errors);
			return;
		}
		const payload: EngineRubricSkillCreatePayload | EngineRubricSkillUpdatePayload = {
			...this.form.value,
		};
		try {
			this.onSubmitEmitter.emit(payload);
			this.clear();
		} catch (error) {
			console.error('Error submitting EngineRubricSkill:', error);
		}
	}

	public clear(): void {
		this.form.reset({
			engine_name: '',
			rubric_code: this.rubric?.code || '',
			skill_code: '',
			assessment: '',
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
